import { Box, Divider } from '@mui/material';
import ToggleMoreFiltersButton from '@shared/components/others/ToggleMoreFiltersButton/ToggleMoreFiltersButton';
import UtmsButton from '@shared/components/ui/UtmsButton';
import { ColumnFilters, RequestStatusEnum, UnknownObject } from '@shared/types';
import { MRT_TableInstance, MRT_RowData } from 'material-react-table';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import ModalDelete from '@shared/components/modals/ModalDelete';
import { useDispatch, useSelector } from 'react-redux';
import {
    addDefectReset,
    closeDefectsRequest,
    closeDefectsReset,
    deleteDefectsRequest,
    deleteDefectsReset,
} from '../store';
import ModalConfirm from '@shared/components/modals/ModalConfirm';
import { DefectStatusEnum } from '../types';
import ModalErrorContainer from '@shared/components/modals/ModalErrorContainer';
import {
    selectAddDefectError,
    selectAddDefectStatus,
    selectCloseDefectsStatus,
    selectDeleteDefectsError,
    selectDeleteDefectsStatus,
} from '../store/selectors';
import ModalSuccessContainer from '@shared/components/modals/ModalSuccessContainer';
import { useNavigate } from 'react-router-dom';
import { UI_DEFECTS_PATH } from '@shared/constants/pathsConstants';
import { editTestCaseRequest } from '@pages/testcases/store';
import DefectLinkDialog from './DefectLinkDialog';
import { selectTestCaseDataActual } from '@pages/testcases/store/selectors';
import ComponentWithChunks from '@shared/components/others/ComponentWithChunk/ComponentWithChunk';
import { useTestcasesChunk } from '@pages/testcases/hooks/useTestcasesChunk';

interface DefectsTableToolbarProps {
    table: MRT_TableInstance<MRT_RowData>;
    columnFilters: ColumnFilters;
    rowSelection: UnknownObject;
    handleOpenDefectAddDialog: () => void;
    testcaseId?: number;
}

const DefectsTableToolbar = (props: DefectsTableToolbarProps) => (
    <ComponentWithChunks
        hooksArray={[useTestcasesChunk]}
        featuresArray={['testCases']}
        Component={DefectsTableToolbarComponent}
        {...props}
    />
);

const DefectsTableToolbarComponent: FC<DefectsTableToolbarProps> = ({
    table,
    columnFilters,
    rowSelection,
    handleOpenDefectAddDialog,
    testcaseId,
}) => {
    const { t } = useTranslation();
    const [openDeleteDefectsModal, setOpenDeleteDefectsModal] = useState(false);
    const [openCloseDefectsModal, setOpenCloseDefectsModal] = useState(false);
    const dispatch = useDispatch();
    const deleteDefectsStatus = useSelector(selectDeleteDefectsStatus);
    const closeDefectsStatus = useSelector(selectCloseDefectsStatus);
    const addDefectStatus = useSelector(selectAddDefectStatus);
    const ids = Object.keys(rowSelection).map((item) => Number(item));
    const navigate = useNavigate();
    const [openLinkDialog, setOpenLinkDialog] = useState(false);
    const linkMode = Boolean(testcaseId);
    const [openUnlinkModal, setOpenUnlinkModal] = useState(false);
    const testcase = useSelector(selectTestCaseDataActual);

    const handleOpenUnlinkModal = () => {
        setOpenUnlinkModal(true);
    };

    const handleSubmitUnlinkDefects = () => {
        dispatch(
            //@ts-ignore
            editTestCaseRequest({ id: testcaseId, params: { defects_ids_to_toggle_link: ids } }),
        );
        setOpenUnlinkModal(false);
    };

    const handleOpenLinkDialog = () => {
        setOpenLinkDialog(true);
    };

    const handleOpenDeleteModal = () => {
        setOpenDeleteDefectsModal(true);
    };

    const handleDeleteDefectsSubmit = () => {
        dispatch(deleteDefectsRequest({ ids }));
        navigate(`/${UI_DEFECTS_PATH}`);
    };

    const handleCloseCloseDefectsModal = () => {
        setOpenCloseDefectsModal(false);
    };

    const handleOpenCloseDefectsModal = () => {
        setOpenCloseDefectsModal(true);
    };

    const handleSubmitCloseDefects = () => {
        dispatch(closeDefectsRequest({ ids, status: DefectStatusEnum.CLOSED }));
        handleCloseCloseDefectsModal();
    };

    return (
        <Box
            sx={{
                display: 'flex',
                gap: 2,
                rowGap: 1,
                mb: 2,
                flexWrap: 'wrap',
            }}
        >
            <UtmsButton onClick={linkMode ? handleOpenLinkDialog : handleOpenDefectAddDialog} startIcon={<AddIcon />}>
                {t(linkMode ? 'link' : 'add')}
            </UtmsButton>
            <UtmsButton
                onClick={handleOpenCloseDefectsModal}
                startIcon={<CloseIcon />}
                disabled={!Boolean(Object.keys(rowSelection).length)}
            >
                {t('close')}
            </UtmsButton>
            <UtmsButton
                onClick={linkMode ? handleOpenUnlinkModal : handleOpenDeleteModal}
                startIcon={<DeleteIcon />}
                disabled={!Boolean(Object.keys(rowSelection).length)}
            >
                {t(linkMode ? 'unlink' : 'delete')}
            </UtmsButton>
            <Divider flexItem orientation="vertical" />
            <ToggleMoreFiltersButton table={table} hasFilters={Boolean(columnFilters.length)} />
            <ModalDelete
                open={openDeleteDefectsModal}
                setOpen={setOpenDeleteDefectsModal}
                handleSubmit={handleDeleteDefectsSubmit}
                objectName={t('defects').toLowerCase()}
            />
            <ModalConfirm
                open={openCloseDefectsModal}
                handleClose={handleCloseCloseDefectsModal}
                handleSubmit={handleSubmitCloseDefects}
                question={t('want_to_close_defects')}
                message={t('it_will_be_impossible_to_undo_this_action')}
                action={t('close')}
                actionIcon={<CloseIcon />}
            />
            <ModalConfirm
                open={openUnlinkModal}
                handleClose={() => setOpenUnlinkModal(false)}
                handleSubmit={handleSubmitUnlinkDefects}
                question={t('want_to_unlink_defects')}
                message={t('it_will_be_impossible_to_undo_this_action')}
                action={t('unlink')}
                actionIcon={<DeleteIcon />}
            />
            {deleteDefectsStatus === RequestStatusEnum.Error && (
                <ModalErrorContainer
                    object={t('defects_r')}
                    action={t('deleting')}
                    resetActionCallback={deleteDefectsReset}
                    errorSelector={selectDeleteDefectsError}
                />
            )}
            {deleteDefectsStatus === RequestStatusEnum.Success && (
                <ModalSuccessContainer
                    object={t('defects')}
                    action={t('deleted_items')}
                    resetActionCallback={deleteDefectsReset}
                />
            )}
            {closeDefectsStatus === RequestStatusEnum.Error && (
                <ModalErrorContainer
                    object={t('defects_r')}
                    action={t('closing')}
                    resetActionCallback={deleteDefectsReset}
                    errorSelector={selectDeleteDefectsError}
                />
            )}
            {closeDefectsStatus === RequestStatusEnum.Success && (
                <ModalSuccessContainer
                    object={t('defects')}
                    action={t('closed_items')}
                    resetActionCallback={closeDefectsReset}
                />
            )}
            {addDefectStatus === RequestStatusEnum.Error && (
                <ModalErrorContainer
                    object={t('defect_r')}
                    action={t('adding')}
                    resetActionCallback={addDefectReset}
                    errorSelector={selectAddDefectError}
                />
            )}
            {addDefectStatus === RequestStatusEnum.Success && (
                <ModalSuccessContainer object={t('defect')} action={t('added')} resetActionCallback={addDefectReset} />
            )}
            {testcaseId && (
                <DefectLinkDialog
                    open={openLinkDialog}
                    onClose={() => setOpenLinkDialog(false)}
                    entityId={testcase.pk}
                    entityType="testcase"
                    defects={testcase.defects}
                />
            )}
        </Box>
    );
};

export default DefectsTableToolbar;
